import React, { FC, useMemo } from "react";
import { Segment, Table, Checkbox, Grid, Loader } from "semantic-ui-react";

import * as tableShader from "../../../Lumber/TableShader";
import * as valuePrecisionConverter from "../../../Lumber/ValuePrecisionConverter";

import _ from "lodash";
import { std } from "mathjs";
import { useProtocolAnalysis } from "./ProtocolAnalysisContext";

import type {
	ProtocolSiteSpecificSummaryTableProps,
	SingleTrialLetteringPair,
	SiteSpecificSummaryTableRow,
	TreatmentCheckedState,
} from "./types";
import sharedStyles from "./ProtocolAnalysis.module.css";

/**
 * ValuePrecisionConverter is untyped JS (getPrecision infers as returning `any`). It returns falsy
 * input unchanged and rounds anything else to a number.
 */
const getPrecision = valuePrecisionConverter.getPrecision as (value: number) => number;

/** A site row that made it past the `average !== null` filter. */
type AveragedSummaryRow = SiteSpecificSummaryTableRow & { average: number };

/** One `meanData` entry per trial, built by calculateMeanAndStandardDeviation. */
type SummaryMeanRow = {
	assessmentId: string;
	plannedTimingId: string | null;
	/** 0 when every value was 0 or null. */
	average: number;
	standardDeviation: number;
	trialId: string;
	/** Undefined when tukeyData has no row for the trial. */
	pValue?: string | null;
	cv?: string | null;
};

type SiteSpecificSummaryTableData = {
	tableHeaderData: AveragedSummaryRow[];
	tableData: _.Dictionary<AveragedSummaryRow[]>;
	meanData: SummaryMeanRow[];
};

/** Builds one SummaryMeanRow per trial, with its p-value and CV looked up from `tukeyData`. */
function calculateMeanAndStandardDeviation(
	groupedTrials: _.Dictionary<AveragedSummaryRow[]>,
	tukeyData: SingleTrialLetteringPair[],
): SummaryMeanRow[] {
	return _.map(groupedTrials, (gt, trialId) => {
		const values = _.map(
			_.filter(gt, (d) => d.excluded === false),
			"average",
		);
		const meanComparisonValue = _.find(tukeyData, (td) => td.trialId === trialId);
		const base = {
			assessmentId: gt[0].assessmentId,
			plannedTimingId: gt[0].plannedTimingId,
			trialId,
			pValue: meanComparisonValue?.pValue,
			cv: meanComparisonValue?.cv,
		};

		if (_.every(values, (v) => v === 0 || v === null)) {
			return { ...base, average: 0, standardDeviation: 0 };
		}
		const mean = _.meanBy(values);
		const standardDeviation = (std(values) / mean) * 100;
		return { ...base, average: getPrecision(mean), standardDeviation: getPrecision(standardDeviation) };
	});
}

/**
 * Sets each row's `tukeyLetters` from `tukeyData`. A trial whose included treatments are all
 * lettered 'a' has no meaningful grouping, so its letters become '-'. Mutates `tableData`'s rows,
 * which buildSiteSpecificSummaryTableData has already cloned.
 */
function applyTukeyLetters(
	tableData: _.Dictionary<AveragedSummaryRow[]>,
	tableHeaderData: AveragedSummaryRow[],
	tukeyData: SingleTrialLetteringPair[],
) {
	_.forEach(tableData, (rows, trialTreatmentId) => {
		_.forEach(rows, (row) => {
			row.tukeyLetters = _.find(
				tukeyData,
				(td) => td.trialId === row.trialId && Number(td.trialTreatmentId) === Number(trialTreatmentId),
			)?.tukeyLetters;
		});
	});

	_.forEach(tableHeaderData, (thd) => {
		const checkedTrialData = _.filter(
			_.map(tableData, (rows) => _.find(rows, (row) => row.trialId === thd.trialId)),
			(row): row is AveragedSummaryRow => !!row && row.average !== null && !row.excluded,
		);
		if (_.every(checkedTrialData, ["tukeyLetters", "a"])) {
			_.forEach(checkedTrialData, (row) => {
				row.tukeyLetters = "-";
			});
		}
	});
}

/**
 * Derives everything ProtocolSiteSpecificSummaryTable renders. Pure: rows are cloned before the
 * shader and tukey letters write onto them, so `siteSpecificData` (a prop) is never mutated.
 */
// Exported for tests.
export function buildSiteSpecificSummaryTableData(
	siteSpecificData: SiteSpecificSummaryTableRow[],
	treatmentsChecked: TreatmentCheckedState[] | null,
	tukeyData: SingleTrialLetteringPair[],
): SiteSpecificSummaryTableData {
	// Filter out trials with no selected assessment data
	const orderedData = _.map(
		_.filter(
			_.sortBy(siteSpecificData, ["country", "region", "city", "trialName", "cooperatorName", "trialId"]),
			(d): d is AveragedSummaryRow => d.average !== null,
		),
		(d) => ({ ...d }),
	);

	// Filter out unchecked treatments to re-color cells
	let filteredOrderedData: AveragedSummaryRow[] = [];
	if ((treatmentsChecked?.length ?? 0) > 0) {
		filteredOrderedData = _.filter(orderedData, (od) => {
			return !!_.find(treatmentsChecked, (tc) => tc.trialTreatmentId === od.trialTreatmentId)?.treatmentChecked;
		});
	}

	const tableHeaderData = _.uniqBy(filteredOrderedData, "trialId");
	const groupedTrials = _.groupBy(
		_.filter(filteredOrderedData, (d) => d.plots?.length > 0),
		"trialId",
	);
	const meanData = calculateMeanAndStandardDeviation(groupedTrials, tukeyData);

	if (Object.keys(groupedTrials).length > 0) {
		if (groupedTrials[Object.keys(groupedTrials)[0]][0]?.assessmentName.includes("MG50")) {
			tableShader.calculateShadingForGroupedTreatmentReversed(groupedTrials, "average", "87, 184, 120");
		} else {
			tableShader.calculateShadingForGroupedTreatment(groupedTrials, "average", "87, 184, 120");
		}
	}

	const tableData = _.groupBy(orderedData, "trialTreatmentId");
	if (tukeyData.length > 0) {
		applyTukeyLetters(tableData, tableHeaderData, tukeyData);
	}

	return { tableHeaderData, tableData, meanData };
}

const ProtocolSiteSpecificSummaryTable: FC<ProtocolSiteSpecificSummaryTableProps> = ({
	siteSpecificData,
	updateCheckedTrial,
	appendedPercent,
	disableCheckBoxes,
	tukeyData,
	tukeyDataRunning,
	treatmentsChecked,
	hideUncheckedTreatmentData,
	collapseView = false,
}) => {
	const {
		pending: { selectedTrialIds },
	} = useProtocolAnalysis();
	const isTrialChecked = (trialId: string) => (selectedTrialIds ? selectedTrialIds.includes(trialId) : true);

	const { tableHeaderData, tableData, meanData } = useMemo(
		() => buildSiteSpecificSummaryTableData(siteSpecificData, treatmentsChecked, tukeyData),
		[siteSpecificData, treatmentsChecked, tukeyData],
	);

	const toggleAll = _.every(tableHeaderData, (thd) => isTrialChecked(thd.trialId));

	return (
		<Segment basic style={{ width: "fit-content" }}>
			<Table celled fixed striped selectable color="blue" textAlign="center" style={{ width: "fit-content" }}>
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell className={sharedStyles.rowTitle} style={{ textAlign: "center" }}>
							<Checkbox
								label="Check All"
								checked={toggleAll}
								disabled={disableCheckBoxes}
								onClick={() => {
									if (!disableCheckBoxes) {
										updateCheckedTrial(!toggleAll, _.map(tableHeaderData, "trialId"));
									}
								}}
							/>
						</Table.HeaderCell>
						{_.reduce<AveragedSummaryRow, React.ReactNode[]>(
							tableHeaderData,
							(acc, thd) => {
								const isChecked = isTrialChecked(thd.trialId);

								if (!collapseView || isChecked) {
									acc.push(
										<Table.HeaderCell key={thd.trialId} style={{ width: 152 }}>
											<Checkbox
												checked={isChecked}
												disabled={disableCheckBoxes}
												onChange={() => {
													updateCheckedTrial(!isChecked, thd.trialId);
												}}
											/>
										</Table.HeaderCell>,
									);
								}
								return acc;
							},
							[],
						)}
					</Table.Row>
					<Table.Row>
						<Table.HeaderCell className={sharedStyles.rowTitle}>Trial Name</Table.HeaderCell>
						{_.reduce<AveragedSummaryRow, React.ReactNode[]>(
							tableHeaderData,
							(acc, thd) => {
								if (!collapseView || thd.trialChecked) {
									acc.push(
										<Table.HeaderCell style={{ fontWeight: "400", background: "white" }} key={thd.trialId}>
											{/* TODO(types): trialName is nullable, and replaceAll on null throws. */}
											<span
												dangerouslySetInnerHTML={{
													__html: thd.trialName ? thd.trialName.replaceAll("_", "_<wbr/>") : "",
												}}
											/>
										</Table.HeaderCell>,
									);
								}
								return acc;
							},
							[],
						)}
					</Table.Row>
					{tableHeaderData?.some((thd) => thd.cooperatorName) && (
						<Table.Row>
							<Table.HeaderCell className={sharedStyles.rowTitle}>Cooperator Name</Table.HeaderCell>
							{_.reduce<AveragedSummaryRow, React.ReactNode[]>(
								tableHeaderData,
								(acc, thd) => {
									if (!collapseView || thd.trialChecked) {
										acc.push(
											<Table.HeaderCell style={{ fontWeight: "400", background: "white" }} key={thd.trialId}>
												{thd.cooperatorName}
											</Table.HeaderCell>,
										);
									}
									return acc;
								},
								[],
							)}
						</Table.Row>
					)}
					<Table.Row>
						<Table.HeaderCell className={sharedStyles.rowTitle}>Farm Location</Table.HeaderCell>
						{_.reduce<AveragedSummaryRow, React.ReactNode[]>(
							tableHeaderData,
							(acc, thd) => {
								if (!collapseView || thd.trialChecked) {
									acc.push(
										<Table.HeaderCell style={{ fontWeight: "400", background: "white" }} key={thd.trialId}>
											{thd.city}, {thd.region}, {thd.countryCode}
										</Table.HeaderCell>,
									);
								}
								return acc;
							},
							[],
						)}
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{_.reduce<_.Dictionary<AveragedSummaryRow[]>, React.ReactNode[]>(
						tableData,
						(acc, td, index) => {
							if (!collapseView || td[0].treatmentChecked) {
								acc.push(
									<Table.Row key={index}>
										<Table.Cell className={sharedStyles.rowTitle}>{td[0]?.trialTreatmentId}</Table.Cell>
										{_.reduce<AveragedSummaryRow, React.ReactNode[]>(
											tableHeaderData,
											(acc, thd) => {
												const treatment = _.find(td, { trialId: thd.trialId });
												if (!collapseView || treatment?.trialChecked) {
													acc.push(
														treatment ? (
															<Table.Cell
																key={thd.trialId}
																style={{
																	backgroundColor:
																		!isTrialChecked(treatment.trialId) ||
																		!_.find(
																			treatmentsChecked,
																			(tc) => tc.trialTreatmentId === treatment.trialTreatmentId,
																		)?.treatmentChecked
																			? "lightGray"
																			: treatment.color,
																	width: "150px",
																}}
															>
																<Grid style={{ width: "inherit", height: "inherit" }}>
																	<Grid.Row style={{ padding: "inherit" }}>
																		<Grid.Column width="1" />
																		<Grid.Column width="9" style={{ padding: 0 }}>
																			{!treatment.treatmentChecked
																				? ""
																				: treatment.excluded === true
																					? "-"
																					: getPrecision(treatment.average)}
																			{!treatment.treatmentChecked
																				? ""
																				: appendedPercent && treatment.excluded === false
																					? "%"
																					: ""}
																		</Grid.Column>
																		{
																			<Grid.Column width="3" style={{ padding: 0 }}>
																				{tukeyDataRunning || disableCheckBoxes ? (
																					<Loader active size="tiny" style={{ zIndex: "0" }} />
																				) : treatment.excluded === true ||
																				  !treatment.treatmentChecked ||
																				  !isTrialChecked(treatment.trialId) ||
																				  hideUncheckedTreatmentData ? (
																					""
																				) : (
																					_.find(
																						treatmentsChecked,
																						(tc) => tc.trialTreatmentId === treatment.trialTreatmentId,
																					)?.treatmentChecked && treatment.tukeyLetters
																				)}
																			</Grid.Column>
																		}
																	</Grid.Row>
																</Grid>
															</Table.Cell>
														) : (
															<Table.Cell key={thd.trialId} style={{ width: "150px" }}>
																{"-"}
															</Table.Cell>
														),
													);
												}

												return acc;
											},
											[],
										)}
									</Table.Row>,
								);
							}
							return acc;
						},
						[],
					)}
					<Table.Row>
						<Table.Cell className={sharedStyles.rowTitle}>CV</Table.Cell>
						{_.reduce<SummaryMeanRow, React.ReactNode[]>(
							meanData,
							(acc, md) => {
								const isChecked = selectedTrialIds ? selectedTrialIds.includes(md.trialId) : true;

								if (!collapseView || isChecked) {
									acc.push(
										(_.some(treatmentsChecked, (tc) => !tc.treatmentChecked) && hideUncheckedTreatmentData) ||
											hideUncheckedTreatmentData ? (
											<Table.Cell key={md.trialId}></Table.Cell>
										) : (
											<Table.Cell key={md.trialId}>{Math.abs(Number(md?.cv)).toFixed(2)} %</Table.Cell>
										),
									);
								}
								return acc;
							},
							[],
						)}
					</Table.Row>
					<Table.Row>
						<Table.Cell className={sharedStyles.rowTitle}>P-Value</Table.Cell>
						{_.reduce<SummaryMeanRow, React.ReactNode[]>(
							meanData,
							(acc, md) => {
								const isChecked = selectedTrialIds ? selectedTrialIds.includes(md.trialId) : true;

								if (!collapseView || isChecked) {
									acc.push(
										<Table.Cell key={md.trialId}>
											{(_.some(treatmentsChecked, (tc) => !tc.treatmentChecked) && hideUncheckedTreatmentData) ||
											hideUncheckedTreatmentData
												? ""
												: Number(Number(md?.pValue).toFixed(5)) === 0
													? "<0.00001"
													: Number(Number(md?.pValue).toFixed(5))}
										</Table.Cell>,
									);
								}
								return acc;
							},
							[],
						)}
					</Table.Row>
				</Table.Body>
			</Table>
		</Segment>
	);
};

export default ProtocolSiteSpecificSummaryTable;
